import React, { useEffect, useState } from "react";
import { Trophy, Award, Star } from "lucide-react";
import api, { inr } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

const PERIODS = [["today", "Today"], ["weekly", "This Week"], ["monthly", "This Month"], ["all", "All Time"]];
const BADGE_ICON = { "Top Performer": Trophy, "Top Earner": Award, "Rising Star": Star };

export default function Leaderboard() {
  useSEO({ title: "Top Performers Leaderboard", path: "/leaderboard", description: "See Credit Kendra's top earning referral partners. Join and climb the leaderboard with transparent multi-level rewards.", keywords: "top earners, referral leaderboard, Credit Kendra partners" });
  const [period, setPeriod] = useState("all");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/top-performers?period=${period}&limit=20`).then((r) => setRows(r.data)).catch(() => {}); }, [period]);
  return (
    <div className="max-w-4xl mx-auto px-6 py-14 min-h-[70vh]">
      <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-[#0B223D] flex items-center gap-3"><Trophy className="w-8 h-8 text-amber-500" /> Top Performers</h1>
      <p className="text-slate-500 mt-2">Ranked by real approved &amp; credited earnings only. Pending, rejected or flagged rewards never count.</p>
      <div className="flex gap-2 mt-6 mb-8">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`leaderboard-period-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${period === k ? "bg-[#0B223D] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-12 text-center text-slate-500">
          <Trophy className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          No eligible earners for this period yet. Rankings update automatically from the reward ledger.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((t) => {
            const Icon = BADGE_ICON[t.badge] || Star;
            return (
              <div key={t.rank} data-testid={`leaderboard-row-${t.rank}`}
                className={`flex items-center gap-4 bg-white rounded-2xl border p-4 ${t.rank === 1 ? "border-amber-300 shadow-lg shadow-amber-100" : "border-slate-200"}`}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display font-extrabold ${t.rank === 1 ? "bg-amber-100 text-amber-600" : t.rank <= 3 ? "bg-slate-200 text-slate-600" : "bg-slate-100 text-slate-400"}`}>#{t.rank}</div>
                <div className="flex-1">
                  <p className="font-display font-bold text-[#0B223D]">{t.name}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-brand"><Icon className="w-3.5 h-3.5" /> {t.badge} · {t.referrals} referrals</span>
                </div>
                <span className="font-mono-num font-extrabold text-lg text-[#0B223D]">{inr(t.earnings)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
